import { Box, BoxProps, styled, Typography } from '@mui/material'
import React, { ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { COLORS } from '../../../constants/colors'
import ArrowRightIcon from '../../global/icons/ArrowRightIcon'

interface SidebarButtonProps {
  title: string
  icon?: ReactNode
  path?: string
  isButton: boolean
  isChild?: boolean
}

interface SidebarButtonBox extends BoxProps {
  isActive: boolean
  isButton: boolean
  isChild: boolean
}

const SidebarButton = ({ title, icon, path, isButton = false, isChild = false }: SidebarButtonProps) => {
  const location = useLocation()
  const navigate = useNavigate()

  const isActive = location.pathname.includes(path!)

  const onNavigate = () => path && navigate(path)

  return (
    <Box paddingX={4}>
      <SidebarButtonBox isActive={isActive} isButton={isButton} onClick={onNavigate} isChild={isChild}>
        <Box display='flex' alignItems='center'>
          {isChild ? (
            <DotBox>
              <Dot className='dot' />
            </DotBox>
          ) : (
            <IconBox bgcolor={isActive && !isChild ? COLORS.white : COLORS.grey150}>{icon}</IconBox>
          )}
          <Typography marginLeft={4} variant='body1'>
            {title}
          </Typography>
        </Box>
        {isButton && <ArrowRightIcon />}
      </SidebarButtonBox>
    </Box>
  )
}

const dotBoxSize = '36px'
const DotBox = styled(Box)({
  width: dotBoxSize,
  height: dotBoxSize,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
})

const dotSize = '4px'
const Dot = styled(Box)(() => ({
  width: dotSize,
  height: dotSize,
  borderRadius: '50%',
  transition: 'background-color .3s'
}))

const iconBoxSize = '36px'
const IconBox = styled(Box)({
  width: iconBoxSize,
  height: iconBoxSize,
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
})

const SidebarButtonBox = styled(Box, {
  shouldForwardProp: (props) => !['isActive', 'isChild', 'isButton'].includes(props as string)
})<SidebarButtonBox>(({ theme, isButton, isActive, isChild }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2),
  borderRadius: theme.spacing(4),
  justifyContent: 'space-between',
  cursor: isButton ? 'pointer' : 'auto',
  color: isActive ? COLORS.blue500 : 'black',
  transition: 'all .3s',
  backgroundColor: isActive ? COLORS.blue300 : '',
  '& div p': {
    fontWeight: isActive ? 600 : isChild ? 'normal' : 500
  },
  '& div div .dot': {
    backgroundColor: isActive ? COLORS.blue500 : COLORS.white
  },
  '&:hover': {
    backgroundColor: isButton && COLORS.blue300,
    color: isButton && COLORS.blue500
  },
  '& .arrow-right > path': {
    stroke: isActive ? COLORS.blue500 : COLORS.white,
    transition: 'stroke .3s'
  },
  '&:hover .arrow-right > path': {
    stroke: COLORS.blue500
  },
  '&:hover div div .dot': {
    backgroundColor: COLORS.blue500
  }
}))

export default SidebarButton
